import React, { useState, useEffect, useMemo } from 'react';
import UnifiedHeader from '../common/UnifiedHeader';
import UnifiedFooter from '../common/UnifiedFooter';
import EcommerceCartDrawer from '../ecommerce/EcommerceCartDrawer';
import EcommerceAuthModal from '../ecommerce/EcommerceAuthModal';
import EcommerceWhatsAppFloating from '../ecommerce/EcommerceWhatsAppFloating';
import EcommerceCheckoutModal from '../ecommerce/EcommerceCheckoutModal';
import { useWebStore } from '../../context/WebStoreContext';

const DondeEstamosPage = ({ user, onLogout }) => {
  const { storeConfig, landingConfig: globalLandingConfig, rubros, ecomCustomer: contextCustomer, setEcomCustomer: setContextCustomer, logoutCustomer } = useWebStore();
  const [config, setConfig] = useState(globalLandingConfig || null);

  useEffect(() => {
    if (globalLandingConfig) {
      setConfig(globalLandingConfig);
    }
  }, [globalLandingConfig]);

  const [cartItems, setCartItems] = useState(() => {
    try {
      const saved = localStorage.getItem('sigecom_ecom_cart');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);
  const [ecomCustomer, setEcomCustomer] = useState(() => {
    try {
      const saved = localStorage.getItem('sigecom_ecom_customer');
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  });

  // Mantener ecomCustomer sincronizado con el contexto global
  useEffect(() => {
    setEcomCustomer(contextCustomer || null);
  }, [contextCustomer]);

  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [authModalMode, setAuthModalMode] = useState('register');

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const totalCartCount = cartItems.reduce((acc, item) => acc + (Number(item.quantity) || 1), 0);
  const totalCartPrice = cartItems.reduce((acc, item) => acc + ((Number(item.unitPrice) || 0) * (Number(item.quantity) || 1)), 0);
  const activeCustomer = ecomCustomer || (user?.email ? { d_razon: user.username, d_email: user.email } : null);
  const canSeePrices = Boolean(storeConfig?.commercial?.publicPricesEnabled || activeCustomer);

  const contact = useMemo(() => config?.contactSection || {
    address: 'Vicente Damonte 1237 / 1247, Lanús Este (B1824ALG), Buenos Aires',
    phone: '(011) 4247-6622',
    whatsapp: '5491142476622',
    email: 'ventas@hiperpel.com.ar',
    schedule: 'Lunes a Viernes de 08:00 a 18:00 hs | Sábados de 08:00 a 13:00 hs'
  }, [config?.contactSection]);

  const locations = useMemo(() => {
    const raw = config?.locationSection?.locations;
    if (Array.isArray(raw) && raw.length > 0) {
      const active = raw.filter(l => l.is_active !== false);
      if (active.length > 0) return active;
    }
    return [
      {
        id: 'loc-default',
        title: config?.locationSection?.facilityTitle || 'Planta de Fabricación y Retiro',
        badge: 'Casa Central & Depósito',
        address: contact.address,
        description: config?.locationSection?.facilityDescription || 'Contamos con amplias instalaciones para la elaboración, stock y despacho ágil de mercadería con estacionamiento y zona de carga para utilitarios y camiones.',
        phone: contact.phone,
        whatsapp: contact.whatsapp,
        schedule: contact.schedule
      }
    ];
  }, [config?.locationSection, contact]);

  const [activeLocIndex, setActiveLocIndex] = useState(0);
  const activeLocation = locations[activeLocIndex] || locations[0];

  const carAccessText = config?.locationSection?.carAccess || 'Fácil acceso conectando directamente con las principales avenidas y autopistas. Zona de carga para retiros.';
  const trainAccessText = config?.locationSection?.trainAccess || 'A pocas cuadras de las principales estaciones de ferrocarril y centros de transbordo.';
  const busAccessText = config?.locationSection?.busAccess || 'Múltiples líneas de colectivo y transporte público con parada directa en las inmediaciones de la planta.';

  return (
    <div className="webstore-site-wrapper min-vh-100 d-flex flex-column" style={{ fontFamily: 'Inter, system-ui, -apple-system, sans-serif', backgroundColor: 'var(--ecom-bg, #f8fafc)', color: 'var(--ecom-text, #1e293b)' }}>
      
      {/* Cabecera Unificada */}
      <UnifiedHeader
        activePage="donde-estamos"
        cartCount={totalCartCount}
        cartTotal={totalCartPrice}
        currentUser={activeCustomer}
        onOpenCart={() => setIsCartOpen(true)}
        onLogout={() => {
          if (logoutCustomer) {
            logoutCustomer(true);
          } else {
            localStorage.removeItem('sigecom_ecom_customer');
            localStorage.removeItem('sigecom_ecom_last_activity');
            setEcomCustomer(null);
          }
        }}
        onAuthClick={(mode) => {
          setAuthModalMode(mode || 'register');
          setIsAuthModalOpen(true);
        }}
        priceListInfo="Lista Minorista"
        canSeePrices={canSeePrices}
        topBarConfig={config?.topBar}
        storeConfig={storeConfig}
        rubros={rubros}
      />

      {/* Banner Superior */}
      <div 
        className="text-white py-4 mb-4"
        style={{
          background: 'linear-gradient(135deg, var(--ecom-primary, #0d6efd) 0%, var(--ecom-secondary, #0a58ca) 100%)'
        }}
      >
        <div className="container">
          <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-2">
            <div>
              <span className="badge bg-warning text-dark fw-bold px-3 py-1 rounded-pill text-uppercase mb-2">
                Ubicación & Logística
              </span>
              <h1 className="fw-black fs-2 mb-0">Dónde Estamos</h1>
            </div>
            <nav aria-label="breadcrumb">
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item"><a href="/" className="text-white opacity-75 text-decoration-none">Inicio</a></li>
                <li className="breadcrumb-item active text-white fw-bold" aria-current="page">Dónde Estamos</li>
              </ol>
            </nav>
          </div>
        </div>
      </div>

      {/* Contenido Principal de Ubicación */}
      <main className="container flex-grow-1 py-4">

        {/* Selector de Sedes / Sucursales si hay más de 1 */}
        {locations.length > 1 && (
          <div className="mb-4">
            <label className="small fw-bold text-muted text-uppercase mb-2 d-block" style={{ letterSpacing: '0.5px' }}>
              Seleccionar Sede / Sucursal ({locations.length} ubicaciones):
            </label>
            <div className="d-flex flex-wrap gap-2 p-2 bg-light rounded-4 border">
              {locations.map((loc, idx) => (
                <button
                  key={loc.id || idx}
                  type="button"
                  className={`btn btn-sm rounded-pill px-3.5 py-2 fw-bold transition-all d-flex align-items-center gap-2 border-0 ${
                    activeLocIndex === idx ? 'btn-primary shadow-sm' : 'bg-white text-dark shadow-xs'
                  }`}
                  onClick={() => setActiveLocIndex(idx)}
                >
                  <i className="bi bi-geo-alt-fill"></i>
                  <span>{loc.title || `Sede #${idx + 1}`}</span>
                  {loc.badge && (
                    <span className={`badge rounded-pill ${activeLocIndex === idx ? 'bg-white text-primary' : 'bg-primary-subtle text-primary'}`} style={{ fontSize: '0.72rem' }}>
                      {loc.badge}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="row g-4 mb-5">
          
          {/* Tarjeta de Información de la Sede Activa */}
          <div className="col-lg-5 col-12">
            <div className="p-4 p-lg-5 bg-light rounded-4 border h-100 d-flex flex-column justify-content-between">
              <div>
                <span className="badge bg-primary-subtle text-primary fw-bold px-3 py-1 rounded-pill text-uppercase mb-3">
                  {activeLocation.badge || 'Sede Comercial'}
                </span>
                <h3 className="fw-bold text-dark mb-3">{activeLocation.title || 'Nuestra Ubicación'}</h3>
                {activeLocation.description && (
                  <p className="text-secondary small mb-4 lh-base">
                    {activeLocation.description}
                  </p>
                )}

                <div className="d-flex flex-column gap-3 mb-4">
                  {activeLocation.address && (
                    <div className="d-flex align-items-start gap-3">
                      <div className="bg-primary-subtle text-primary rounded-circle d-flex align-items-center justify-content-center flex-shrink-0" style={{ width: '38px', height: '38px', fontSize: '1.05rem' }}>
                        <i className="bi bi-geo-alt-fill"></i>
                      </div>
                      <div>
                        <h6 className="fw-bold mb-1 text-dark">Dirección</h6>
                        <span className="text-secondary small">{activeLocation.address}</span>
                      </div>
                    </div>
                  )}

                  {activeLocation.schedule && (
                    <div className="d-flex align-items-start gap-3">
                      <div className="bg-primary-subtle text-primary rounded-circle d-flex align-items-center justify-content-center flex-shrink-0" style={{ width: '38px', height: '38px', fontSize: '1.05rem' }}>
                        <i className="bi bi-clock-fill"></i>
                      </div>
                      <div>
                        <h6 className="fw-bold mb-1 text-dark">Horarios de Atención y Retiros</h6>
                        <span className="text-secondary small">{activeLocation.schedule}</span>
                      </div>
                    </div>
                  )}

                  {activeLocation.phone && (
                    <div className="d-flex align-items-start gap-3">
                      <div className="bg-primary-subtle text-primary rounded-circle d-flex align-items-center justify-content-center flex-shrink-0" style={{ width: '38px', height: '38px', fontSize: '1.05rem' }}>
                        <i className="bi bi-telephone-fill"></i>
                      </div>
                      <div>
                        <h6 className="fw-bold mb-1 text-dark">Teléfono de Contacto</h6>
                        <a href={`tel:${activeLocation.phone}`} className="text-primary fw-semibold small text-decoration-none">{activeLocation.phone}</a>
                      </div>
                    </div>
                  )}

                  {activeLocation.whatsapp && (
                    <div className="d-flex align-items-start gap-3">
                      <div className="bg-success-subtle text-success rounded-circle d-flex align-items-center justify-content-center flex-shrink-0" style={{ width: '38px', height: '38px', fontSize: '1.05rem' }}>
                        <i className="bi bi-whatsapp"></i>
                      </div>
                      <div>
                        <h6 className="fw-bold mb-1 text-dark">WhatsApp Ventas Directas</h6>
                        <a href={`https://wa.me/${activeLocation.whatsapp.replace(/\D/g, '')}`} target="_blank" rel="noreferrer" className="text-success fw-semibold small text-decoration-none">
                          +{activeLocation.whatsapp}
                        </a>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {activeLocation.address && (
                <div className="pt-3 border-top">
                  <a 
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(activeLocation.address)}`} 
                    target="_blank" 
                    rel="noreferrer" 
                    className="btn btn-primary fw-bold rounded-pill w-100 py-2.5 shadow-sm d-flex align-items-center justify-content-center gap-2"
                  >
                    <i className="bi bi-map-fill"></i> Abrir en Google Maps
                  </a>
                </div>
              )}
            </div>
          </div>

          {/* Mapa Interactivo / Embed */}
          <div className="col-lg-7 col-12">
            <div className="rounded-4 overflow-hidden shadow-sm border h-100 bg-light" style={{ minHeight: '450px' }}>
              <iframe
                key={activeLocation.id || activeLocIndex}
                title={`Ubicación ${activeLocation.title || 'Hiperpel'}`}
                src={`https://maps.google.com/maps?q=${encodeURIComponent(activeLocation.address || contact.address)}&t=&z=15&ie=UTF8&iwloc=&output=embed`}
                width="100%"
                height="100%"
                style={{ border: 0, minHeight: '450px' }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              ></iframe>
            </div>
          </div>

        </div>

        {/* Todas las Sedes en Tarjetas si hay más de 1 */}
        {locations.length > 1 && (
          <div className="mb-5">
            <div className="d-flex align-items-center justify-content-between mb-3">
              <h5 className="fw-bold text-dark mb-0">Todas Nuestras Sedes y Puntos de Atención</h5>
              <span className="text-muted small">Hacé clic en cualquier sede para enfocarla en el mapa</span>
            </div>
            <div className="row g-3">
              {locations.map((loc, idx) => (
                <div key={loc.id || idx} className="col-12 col-md-6 col-lg-4">
                  <div 
                    className={`card rounded-4 p-3 border transition-all cursor-pointer h-100 ${
                      activeLocIndex === idx ? 'border-primary bg-primary-subtle shadow-sm' : 'bg-white shadow-xs'
                    }`}
                    style={{ cursor: 'pointer' }}
                    onClick={() => {
                      setActiveLocIndex(idx);
                      window.scrollTo({ top: 120, behavior: 'smooth' });
                    }}
                  >
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <span className="badge bg-primary text-white font-monospace fw-bold px-2 py-1 rounded-pill" style={{ fontSize: '0.72rem' }}>
                        #{idx + 1}
                      </span>
                      {loc.badge && (
                        <span className="badge bg-white text-dark border small">{loc.badge}</span>
                      )}
                    </div>
                    <h6 className="fw-bold text-dark mb-1">{loc.title}</h6>
                    <p className="text-muted small mb-2 flex-grow-1" style={{ fontSize: '0.8rem' }}>
                      <i className="bi bi-geo-alt me-1 text-primary"></i>{loc.address}
                    </p>
                    {loc.schedule && (
                      <div className="text-muted small mb-3" style={{ fontSize: '0.75rem' }}>
                        <i className="bi bi-clock me-1"></i>{loc.schedule}
                      </div>
                    )}
                    <button
                      type="button"
                      className={`btn btn-sm rounded-pill fw-semibold w-100 ${activeLocIndex === idx ? 'btn-primary' : 'btn-outline-primary'}`}
                    >
                      {activeLocIndex === idx ? 'Sede Seleccionada' : 'Ver Mapa y Datos'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Accesos y Medios de Transporte */}
        <div className="row g-4 mb-4">
          <div className="col-md-4">
            <div className="p-4 bg-white border rounded-4 shadow-xs h-100">
              <div className="bg-primary-subtle text-primary rounded-circle d-flex align-items-center justify-content-center mb-3" style={{ width: '42px', height: '42px', fontSize: '1.2rem' }}>
                <i className="bi bi-car-front-fill"></i>
              </div>
              <h5 className="fw-bold text-dark mb-2 fs-6">En Automóvil / Utilitario / Camión</h5>
              <p className="text-muted small mb-0 lh-base">
                {carAccessText}
              </p>
            </div>
          </div>
          <div className="col-md-4">
            <div className="p-4 bg-white border rounded-4 shadow-xs h-100">
              <div className="bg-success-subtle text-success rounded-circle d-flex align-items-center justify-content-center mb-3" style={{ width: '42px', height: '42px', fontSize: '1.2rem' }}>
                <i className="bi bi-train-front-fill"></i>
              </div>
              <h5 className="fw-bold text-dark mb-2 fs-6">Tren / Ferrocarril</h5>
              <p className="text-muted small mb-0 lh-base">
                {trainAccessText}
              </p>
            </div>
          </div>
          <div className="col-md-4">
            <div className="p-4 bg-white border rounded-4 shadow-xs h-100">
              <div className="bg-info-subtle text-info-emphasis rounded-circle d-flex align-items-center justify-content-center mb-3" style={{ width: '42px', height: '42px', fontSize: '1.2rem' }}>
                <i className="bi bi-bus-front-fill text-dark"></i>
              </div>
              <h5 className="fw-bold text-dark mb-2 fs-6">Colectivos y Transporte Público</h5>
              <p className="text-muted small mb-0 lh-base">
                {busAccessText}
              </p>
            </div>
          </div>
        </div>
      </main>

      {/* Footer Unificado */}
      <UnifiedFooter storeConfig={storeConfig} landingConfig={config} />

      {/* Modales Compartidos */}
      <EcommerceCartDrawer
        isOpen={isCartOpen}
        onClose={() => setIsCartOpen(false)}
        cartItems={cartItems}
        onUpdateQuantity={(key, qty) => {
          if (qty <= 0) setCartItems(prev => prev.filter(i => i.cartKey !== key));
          else setCartItems(prev => prev.map(i => i.cartKey === key ? { ...i, quantity: qty } : i));
        }}
        onRemoveItem={(key) => setCartItems(prev => prev.filter(i => i.cartKey !== key))}
        onClearCart={() => setCartItems([])}
        onCheckout={() => {
          setIsCartOpen(false);
          setIsCheckoutOpen(true);
        }}
        canSeePrices={canSeePrices}
        onLoginClick={() => {
          setIsCartOpen(false);
          setAuthModalMode('register');
          setIsAuthModalOpen(true);
        }}
      />

      <EcommerceCheckoutModal
        show={isCheckoutOpen}
        onHide={() => setIsCheckoutOpen(false)}
        cartItems={cartItems}
        canSeePrices={canSeePrices}
        currentUser={activeCustomer}
        whatsappSalesNumber={config?.contactSection?.whatsapp || '5491142476622'}
        companyName={storeConfig?.footer?.companyName || 'HIPERPEL S.A.'}
        storeConfig={storeConfig}
        companyId={storeConfig?.company_id || 1}
        onRequireAuth={() => {
          setIsCheckoutOpen(false);
          setAuthModalMode('register');
          setIsAuthModalOpen(true);
        }}
        onOrderCompleted={() => setCartItems([])}
      />

      <EcommerceAuthModal
        show={isAuthModalOpen}
        onHide={() => setIsAuthModalOpen(false)}
        initialMode={authModalMode}
        onSuccess={(customer) => {
          setEcomCustomer(customer);
          if (setContextCustomer) {
            setContextCustomer(customer);
          } else {
            localStorage.setItem('sigecom_ecom_customer', JSON.stringify(customer));
            localStorage.setItem('sigecom_ecom_last_activity', Date.now().toString());
          }
          setIsAuthModalOpen(false);
        }}
        companyId={storeConfig?.company_id || 1}
      />

      {config?.whatsappFloating?.enabled !== false && (
        <EcommerceWhatsAppFloating
          phoneNumber={config?.whatsappFloating?.phone || config?.contactSection?.whatsapp || '5491142476622'}
          defaultMessage={config?.whatsappFloating?.message || `¡Hola! Me comunico para consultar cómo llegar a la planta de ${config?.logoText || 'la empresa'}.`}
          showPill={config?.whatsappFloating?.showPill !== false}
          pillText={config?.whatsappFloating?.pillText || '¿Dudas? ¡Escribinos!'}
        />
      )}

    </div>
  );
};

export default DondeEstamosPage;
